import React, { useState, useEffect } from "react";
import { X, Play, TrendingDown, TrendingUp, DollarSign, Info } from "lucide-react";
import { WIE_SKILL_KEYS as ALL_SKILLS, WIE_SKILL_LABELS as SKILL_LABELS } from "../../constants/wieSkills";

const fmt  = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 });
const fmtM = (n) => {
  if (n == null || n === 0) return "—";
  return n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;
};

function Delta({ before, after, unit = "", higherIsBetter = false, formatter = fmt }) {
  if (before == null || after == null) return <span className="text-gray-400">—</span>;
  const diff = after - before;
  const improved = higherIsBetter ? diff > 0 : diff < 0;
  const neutral  = Math.abs(diff) < 0.01;
  const color    = neutral ? "text-gray-500" : improved ? "text-emerald-700" : "text-red-700";
  const Icon     = neutral ? null : improved ? TrendingDown : TrendingUp;

  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${color}`}>
      {formatter(before)}{unit}
      <span className="text-gray-300 font-normal mx-0.5">→</span>
      {formatter(after)}{unit}
      {!neutral && Icon && <Icon className="w-3 h-3" />}
    </span>
  );
}

function SkillScenarioRow({ skill, input, onChange, baselineSkill, horizonDays }) {
  const hasInput = input.addFte > 0 || input.automationPct > 0 || input.msspHours > 0;

  // Ramp-up beyond the horizon means the hire isn't productive within the window.
  // Mirror the backend: effective FTE = AddFte × (horizon − min(ramp, horizon)) / horizon.
  const rampDays   = Math.max(0, Math.min(input.rampUpDays || 0, horizonDays));
  const rampFactor = horizonDays > 0 ? (horizonDays - rampDays) / horizonDays : 0;
  const effectiveFte = (input.addFte || 0) * rampFactor;
  const rampKillsHire = (input.addFte || 0) > 0 && effectiveFte <= 0;

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${hasInput ? "border-blue-200" : "border-gray-200"}`}>
      <div className={`flex items-center gap-3 px-4 py-3 ${hasInput ? "bg-blue-50" : "bg-gray-50"}`}>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-800">{SKILL_LABELS[skill]}</span>
          {baselineSkill && (
            <span className="ml-2 text-xs text-gray-400">
              {fmt(baselineSkill.beforeRequiredFte)} FTE required
              {baselineSkill.beforeGapFte > 0 && ` · ${fmt(baselineSkill.beforeGapFte)} FTE gap`}
            </span>
          )}
        </div>
        {hasInput && (
          <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
            Modified
          </span>
        )}
      </div>

      <div className="px-4 py-3 bg-white grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Add FTE */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Add FTE</label>
          <input
            type="number" min="0" max="20" step="0.5"
            value={input.addFte || ""}
            onChange={e => onChange(skill, "addFte", parseFloat(e.target.value) || 0)}
            placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
          {(input.addFte || 0) > 0 && (
            rampKillsHire
              ? <p className="text-[10px] text-amber-600 mt-1">Ramp-up ≥ {horizonDays}d timeline — adds 0 capacity this window</p>
              : rampFactor < 1
                ? <p className="text-[10px] text-gray-400 mt-1">≈ {fmt(effectiveFte)} FTE effective over {horizonDays}d (ramp-up discount)</p>
                : null
          )}
        </div>

        {/* Ramp-up days */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Ramp-up Days</label>
          <input
            type="number" min="0" max={horizonDays} step="15"
            value={input.rampUpDays || ""}
            onChange={e => onChange(skill, "rampUpDays", parseInt(e.target.value) || 0)}
            placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
        </div>

        {/* Automation % */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Automation Reduction <span className="font-semibold text-blue-600">{input.automationPct || 0}%</span>
          </label>
          <input
            type="range" min="0" max="50" step="5"
            value={input.automationPct || 0}
            onChange={e => onChange(skill, "automationPct", parseInt(e.target.value))}
            className="w-full accent-blue-600 mt-1"
          />
        </div>

        {/* MSSP Hours/month */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">MSSP Hours/mo</label>
          <input
            type="number" min="0" max="500" step="10"
            value={input.msspHours || ""}
            onChange={e => onChange(skill, "msspHours", parseFloat(e.target.value) || 0)}
            placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
        </div>
      </div>
    </div>
  );
}

export default function WieScenarioPanel({ onClose, onRun, running, result, horizonDays }) {
  const [inputs, setInputs] = useState(() =>
    Object.fromEntries(ALL_SKILLS.map(s => [s, { addFte: 0, rampUpDays: 0, automationPct: 0, msspHours: 0 }]))
  );

  // Reset inputs when panel opens
  useEffect(() => {
    setInputs(Object.fromEntries(
      ALL_SKILLS.map(s => [s, { addFte: 0, rampUpDays: 0, automationPct: 0, msspHours: 0 }])
    ));
  }, []);

  const updateInput = (skill, field, value) =>
    setInputs(prev => ({ ...prev, [skill]: { ...prev[skill], [field]: value } }));

  const handleRun = () => {
    const skills = ALL_SKILLS.map(skill => ({
      skillArea:     skill,
      addFte:        inputs[skill].addFte,
      rampUpDays:    inputs[skill].rampUpDays,
      automationPct: inputs[skill].automationPct,
      msspHours:     inputs[skill].msspHours,
    }));
    onRun({ horizonDays, skills });
  };

  const hasAnyInput = ALL_SKILLS.some(s =>
    inputs[s].addFte > 0 || inputs[s].automationPct > 0 || inputs[s].msspHours > 0
  );

  // Index result skills by skill area
  const resultBySkill = result
    ? Object.fromEntries(result.skills.map(s => [s.skillArea, s]))
    : {};

  const changedSkills = result?.skills?.filter(s => s.hasChanges) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Play className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">What-If Scenario Planning</h2>
              <p className="text-xs text-gray-500">{horizonDays}-day timeline · Adjust assumptions to see impact</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner */}
        <div className="mx-6 mt-4 mb-2 flex gap-2 bg-purple-50 border border-purple-200 rounded-xl p-3 flex-shrink-0">
          <Info className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-purple-700 leading-relaxed">
            Model the impact of hiring, automation, or managed services on your security gap.
            Only skill areas with changes are included in the comparison.
          </p>
        </div>

        <div className="overflow-y-auto flex-1 px-6 pb-4">

          {/* Skill rows */}
          <div className="space-y-2 mb-6">
            {ALL_SKILLS.map(skill => (
              <SkillScenarioRow
                key={skill}
                skill={skill}
                input={inputs[skill]}
                onChange={updateInput}
                baselineSkill={resultBySkill[skill]}
                horizonDays={horizonDays}
              />
            ))}
          </div>

          {/* Results section */}
          {result && (
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              {/* Summary KPIs */}
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                  Scenario Impact Summary
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-white border border-gray-200 px-3 py-2.5">
                    <div className="text-xs text-gray-500 mb-1">Gap FTE</div>
                    <Delta
                      before={result.beforeTotalGapFte}
                      after={result.afterTotalGapFte}
                      unit=" FTE"
                      higherIsBetter={false}
                    />
                  </div>
                  <div className="rounded-xl bg-white border border-gray-200 px-3 py-2.5">
                    <div className="text-xs text-gray-500 mb-1">Coverage</div>
                    <Delta
                      before={result.beforeCoveragePercent}
                      after={result.afterCoveragePercent}
                      unit="%"
                      higherIsBetter={true}
                    />
                  </div>
                  <div className="rounded-xl bg-white border border-gray-200 px-3 py-2.5">
                    <div className="text-xs text-gray-500 mb-1">Exposure (High)</div>
                    <Delta
                      before={result.beforeExposureHigh}
                      after={result.afterExposureHigh}
                      higherIsBetter={false}
                      formatter={fmtM}
                    />
                  </div>
                  <div className={`rounded-xl border px-3 py-2.5 ${result.totalAnnualCostDelta > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"}`}>
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Annual Cost Add
                    </div>
                    <span className={`text-sm font-bold ${result.totalAnnualCostDelta > 0 ? "text-amber-700" : "text-gray-400"}`}>
                      {result.totalAnnualCostDelta > 0 ? `+${fmtM(result.totalAnnualCostDelta)}` : "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Per-skill breakdown — only changed skills */}
              {changedSkills.length > 0 && (
                <div className="px-5 py-4">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                    Per-Skill Comparison
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left font-semibold text-gray-500 pb-2 pr-4">Skill Area</th>
                          <th className="text-right font-semibold text-gray-500 pb-2 px-3">Required FTE</th>
                          <th className="text-right font-semibold text-gray-500 pb-2 px-3">Gap FTE</th>
                          <th className="text-right font-semibold text-gray-500 pb-2 px-3">Coverage</th>
                          <th className="text-right font-semibold text-gray-500 pb-2 px-3">Exposure (High)</th>
                          <th className="text-right font-semibold text-gray-500 pb-2 pl-3">Cost Add/yr</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changedSkills.map(s => (
                          <tr key={s.skillArea} className="border-b border-gray-50">
                            <td className="py-2 pr-4 font-medium text-gray-800">{s.skillLabel}</td>
                            <td className="py-2 px-3 text-right">
                              <Delta before={s.beforeRequiredFte} after={s.afterRequiredFte} unit=" FTE" higherIsBetter={false} />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Delta before={s.beforeGapFte} after={s.afterGapFte} unit=" FTE" higherIsBetter={false} />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Delta before={s.beforeCoverage} after={s.afterCoverage} unit="%" higherIsBetter={true} />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Delta before={s.beforeExposureHigh} after={s.afterExposureHigh} higherIsBetter={false} formatter={fmtM} />
                            </td>
                            <td className="py-2 pl-3 text-right">
                              {s.annualCostDelta > 0
                                ? <span className="font-semibold text-amber-700">+{fmtM(s.annualCostDelta)}</span>
                                : <span className="text-gray-400">—</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Ramp-up period reduces effective FTE contribution. Automation % reduces required workload hours.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-400">
            Results are not saved — this is a planning simulation only.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleRun}
              disabled={running || !hasAnyInput}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              <Play className={`w-4 h-4 ${running ? "animate-pulse" : ""}`} />
              {running ? "Running…" : "Run Scenario"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
